/**
 * Wave 33 — 10 new cities: Bordeaux 🇫🇷, Essen 🇩🇪, Tirana 🇦🇱,
 * Podgorica 🇲🇪, Skopje 🇲🇰, Surat 🇮🇳, Fukuoka 🇯🇵,
 * Kampala 🇺🇬, Manama 🇧🇭, Montevideo 🇺🇾
 */
import type { CityAgencyData } from './city-agency-pages';

const s = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development with React, Node.js, Python, and cloud-native architecture.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, and dashboards using modern TypeScript stacks.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containerization, monitoring, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, ISO 27001, GDPR, HIPAA, PCI-DSS.' },
];
const ind = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, and compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplaces, inventory management, and omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, and HIPAA-compliant applications.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility.' },
];
const faq = (city: string, country: string, flight: string, tz: string) => [
  { question: `Why choose ${city} for software development outsourcing?`, answer: `${city} projects benefit from ${tz} timezone alignment, 60%+ cost savings vs local agencies, and senior engineers fluent in English and local languages.` },
  { question: `What technologies do your ${city} engineers use?`, answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes, TensorFlow, PyTorch — full-stack and specialized.' },
  { question: 'How do you ensure code quality?', answer: 'Mandatory code reviews, CI/CD pipelines, 80%+ test coverage, SonarQube analysis, and bi-weekly sprint demos.' },
  { question: `Can I visit your office from ${city}?`, answer: `Yes! Our Casablanca HQ is ${flight} from ${city}. We welcome client visits anytime for kickoffs and sprint reviews.` },
  { question: 'What engagement models do you offer?', answer: 'Dedicated teams, staff augmentation, and fixed-price projects. Start with 2 developers, scale to 50+.' },
  { question: `Do you comply with ${country} data protection laws?`, answer: 'Yes. Enterprise security standards, GDPR compliant. NDAs signed, encrypted environments, full regulatory compliance.' },
];

export const CITY_AGENCY_PAGES_WAVE33: Record<string, CityAgencyData> = {

  bordeaux: {
    slug: 'software-development-agency-bordeaux',
    cityName: 'Bordeaux',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Bordeaux | Call IT Dev', fr: 'Agence Développement Informatique Bordeaux | Call IT Dev' },
    description: { en: 'Top software development agency for Bordeaux. SaaS, web, mobile, AI. French-speaking engineers, CET timezone, from €25/hr. 60% savings.', fr: 'Agence de développement informatique pour Bordeaux. SaaS, web, mobile, IA. Ingénieurs francophones, dès 25 €/h. 60% d\'économies.' },
    keywords: ['software development agency Bordeaux', 'agence développement informatique Bordeaux', 'nearshore development France', 'développeur Bordeaux'],
    heroTitle: { en: 'Software Development Agency for Bordeaux', fr: 'Agence de Développement Informatique pour Bordeaux' },
    heroSubtitle: { en: 'Native French engineers at nearshore rates. Same CET timezone, 60% savings vs local Bordeaux agencies.', fr: 'Ingénieurs francophones natifs à tarifs nearshore. Même fuseau CET, 60% d\'économies vs agences bordelaises.' },
    whyTitle: { en: 'Why Bordeaux Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises Bordelaises Choisissent Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Native French Engineers', description: 'All developers speak fluent French. Seamless collaboration with Bordeaux teams.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical working hours as Bordeaux. Real-time collaboration.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €25-40/hr vs €75-110/hr in Bordeaux.' },
      { icon: '✈️', title: '2.5h Direct Flight', description: 'Casablanca to Bordeaux with direct connections.' },
      { icon: '🏗️', title: 'Tech Ecosystem', description: 'Supporting Bordeaux\'s growing tech scene with scalable nearshore teams.' },
      { icon: '🛡️', title: 'RGPD Compliant', description: 'ISO 27001, GDPR/RGPD, French data protection standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Bordeaux', 'France', '2.5h', 'CET'),
    localRate: '€75-110', ourRate: '€25-40', savings: '60%+',
  },

  essen: {
    slug: 'software-development-agency-essen',
    cityName: 'Essen',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Essen | Call IT Dev', de: 'Softwareentwicklung Agentur Essen | Call IT Dev' },
    description: { en: 'Software development agency for Essen. SaaS, web, mobile, AI. German-speaking engineers, CET timezone, from €30/hr. 55% savings.', de: 'Softwareentwicklungsagentur für Essen. SaaS, Web, Mobile, KI. Deutschsprachige Ingenieure, ab 30 €/h. 55% Ersparnis.' },
    keywords: ['software development agency Essen', 'Softwareentwicklung Essen', 'IT outsourcing Ruhrgebiet', 'nearshore development Germany'],
    heroTitle: { en: 'Software Development Agency for Essen', de: 'Softwareentwicklung Agentur für Essen' },
    heroSubtitle: { en: 'German-speaking engineers at nearshore rates. Same CET timezone, 55% savings vs Ruhr area agencies.', de: 'Deutschsprachige Ingenieure zu Nearshore-Preisen. Gleiche CET-Zeitzone, 55% Ersparnis vs Ruhrgebiet.' },
    whyTitle: { en: 'Why Essen Companies Choose Call IT Dev', de: 'Warum Essener Unternehmen Call IT Dev Wählen' },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking Engineers', description: 'Fluent German developers for seamless Ruhr area collaboration.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same working hours as Essen. Real-time standups and meetings.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €30-45/hr vs €80-120/hr in Essen.' },
      { icon: '✈️', title: '4h Flight via Düsseldorf', description: 'Direct connections Casablanca to Düsseldorf airport.' },
      { icon: '🏭', title: 'Industrial Tech', description: 'Supporting Ruhr area industrial digitalization with specialized teams.' },
      { icon: '🛡️', title: 'DSGVO Compliant', description: 'ISO 27001, GDPR/DSGVO, German data protection standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Essen', 'Germany', '4h via Düsseldorf', 'CET'),
    localRate: '€80-120', ourRate: '€30-45', savings: '55%+',
  },

  tirana: {
    slug: 'software-development-agency-tirana',
    cityName: 'Tirana',
    countryName: 'Albania',
    flag: '🇦🇱',
    timezone: 'CET (GMT+1)',
    currency: 'ALL/EUR',
    title: { en: 'Software Development Agency Tirana | Call IT Dev' },
    description: { en: 'Software development agency for Tirana, Albania. SaaS, web, mobile, AI. Multilingual engineers, CET timezone, from $22/hr. 65% savings vs Western Europe.' },
    keywords: ['software development agency Tirana', 'IT outsourcing Albania', 'nearshore development Balkans', 'web development Tirana'],
    heroTitle: { en: 'Software Development Agency for Tirana' },
    heroSubtitle: { en: 'Multilingual engineers at nearshore rates. CET timezone, 65% savings vs Western Europe. Your Balkans tech partner.' },
    whyTitle: { en: 'Why Tirana Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in English, French, Italian, and Albanian.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same working hours as Tirana for real-time collaboration.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $22-38/hr vs $70-110/hr in Western Europe.' },
      { icon: '🇪🇺', title: 'EU Candidate Country', description: 'Albania\'s EU accession path ensures regulatory alignment.' },
      { icon: '🚀', title: 'Growing Tech Hub', description: 'Supporting Tirana\'s digital transformation with scalable teams.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise security standards, GDPR compliant.' },
    ],
    services: s, industries: ind,
    faqs: faq('Tirana', 'Albania', '4h', 'CET'),
    localRate: '$70-110', ourRate: '$22-38', savings: '65%+',
  },

  podgorica: {
    slug: 'software-development-agency-podgorica',
    cityName: 'Podgorica',
    countryName: 'Montenegro',
    flag: '🇲🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Podgorica | Call IT Dev' },
    description: { en: 'Software development agency for Podgorica, Montenegro. SaaS, web, mobile, AI. CET timezone, from $22/hr. 65% savings vs Western Europe.' },
    keywords: ['software development agency Podgorica', 'IT outsourcing Montenegro', 'nearshore development Balkans', 'web development Podgorica'],
    heroTitle: { en: 'Software Development Agency for Podgorica' },
    heroSubtitle: { en: 'Multilingual engineers at competitive rates. CET timezone, 65% savings. Your Montenegro tech partner.' },
    whyTitle: { en: 'Why Podgorica Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in English, French, and regional languages.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same working hours as Podgorica for seamless collaboration.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $22-38/hr vs Western European rates.' },
      { icon: '💶', title: 'Euro Currency', description: 'Montenegro uses EUR — no currency risk for European clients.' },
      { icon: '🚀', title: 'Digital Growth', description: 'Supporting Montenegro\'s digital economy with scalable nearshore teams.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001, GDPR, enterprise-grade security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Podgorica', 'Montenegro', '4.5h', 'CET'),
    localRate: '$70-110', ourRate: '$22-38', savings: '65%+',
  },

  skopje: {
    slug: 'software-development-agency-skopje',
    cityName: 'Skopje',
    countryName: 'North Macedonia',
    flag: '🇲🇰',
    timezone: 'CET (GMT+1)',
    currency: 'MKD/EUR',
    title: { en: 'Software Development Agency Skopje | Call IT Dev' },
    description: { en: 'Software development agency for Skopje, North Macedonia. SaaS, web, mobile, AI. CET timezone, from $22/hr. 65% savings vs Western Europe.' },
    keywords: ['software development agency Skopje', 'IT outsourcing North Macedonia', 'nearshore development Balkans', 'web development Skopje'],
    heroTitle: { en: 'Software Development Agency for Skopje' },
    heroSubtitle: { en: 'Skilled engineers at nearshore rates. CET timezone, 65% savings. Your North Macedonia tech partner.' },
    whyTitle: { en: 'Why Skopje Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in English, French, and Macedonian.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same working hours as Skopje for real-time collaboration.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $22-38/hr vs $70-110/hr in Western Europe.' },
      { icon: '🇪🇺', title: 'EU Candidate', description: 'North Macedonia\'s EU path ensures regulatory convergence.' },
      { icon: '🎓', title: 'Strong STEM Talent', description: 'Growing pool of engineering graduates in the Balkans region.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001, GDPR, enterprise-grade security.' },
    ],
    services: s, industries: ind,
    faqs: faq('Skopje', 'North Macedonia', '4.5h', 'CET'),
    localRate: '$70-110', ourRate: '$22-38', savings: '65%+',
  },

  surat: {
    slug: 'software-development-agency-surat',
    cityName: 'Surat',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: { en: 'Software Development Agency Surat | Call IT Dev' },
    description: { en: 'Software development agency for Surat, India. SaaS, web, mobile, AI. English-speaking engineers, from $18/hr. 70% savings vs US/UK rates.' },
    keywords: ['software development agency Surat', 'IT outsourcing Surat', 'web development Surat', 'software company Surat Gujarat'],
    heroTitle: { en: 'Software Development Agency for Surat' },
    heroSubtitle: { en: 'English-speaking engineers at competitive rates. 70% savings vs US/UK. Gujarat\'s diamond city, tech-enabled.' },
    whyTitle: { en: 'Why Surat Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Speaking Engineers', description: 'Fluent English developers for seamless international collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $18-35/hr vs $80-150/hr in US/UK.' },
      { icon: '🏭', title: 'Industrial Innovation', description: 'Supporting Surat\'s diamond and textile industries with digital transformation.' },
      { icon: '🌏', title: 'Overlap Hours', description: '4-5 hours overlap with European business hours for daily standups.' },
      { icon: '🎓', title: 'Gujarat Tech Talent', description: 'Access to Gujarat\'s growing pool of engineering graduates.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR, SOC 2-aligned security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Surat', 'India', '8h', 'IST with 4-5h European overlap'),
    localRate: '$80-150', ourRate: '$18-35', savings: '70%+',
  },

  fukuoka: {
    slug: 'software-development-agency-fukuoka',
    cityName: 'Fukuoka',
    countryName: 'Japan',
    flag: '🇯🇵',
    timezone: 'JST (GMT+9)',
    currency: 'JPY/USD',
    title: { en: 'Software Development Agency Fukuoka | Call IT Dev', ja: 'ソフトウェア開発会社 福岡 | Call IT Dev' },
    description: { en: 'Software development agency for Fukuoka, Japan. SaaS, web, mobile, AI. Bilingual engineers, from $30/hr. 60% savings vs Tokyo rates.', ja: '福岡のソフトウェア開発会社。SaaS、Web、モバイル、AI開発。バイリンガルエンジニア、30$/時間〜。東京比60%削減。' },
    keywords: ['software development agency Fukuoka', 'ソフトウェア開発 福岡', 'IT outsourcing Fukuoka', 'web development Fukuoka'],
    heroTitle: { en: 'Software Development Agency for Fukuoka', ja: '福岡のソフトウェア開発パートナー' },
    heroSubtitle: { en: 'Bilingual engineers at nearshore rates. 60% savings vs Tokyo. Supporting Fukuoka\'s startup city vision.', ja: 'バイリンガルエンジニアがニアショア価格で。東京比60%削減。福岡のスタートアップ都市ビジョンを支援。' },
    whyTitle: { en: 'Why Fukuoka Companies Choose Call IT Dev', ja: 'なぜ福岡企業がCall IT Devを選ぶのか' },
    whyReasons: [
      { icon: '🗣️', title: 'Bilingual Engineers', description: 'English and Japanese-capable teams for seamless communication.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at $30-50/hr vs $80-150/hr in Tokyo.' },
      { icon: '🚀', title: 'Startup City Partner', description: 'Supporting Fukuoka\'s National Strategic Special Zone for startups.' },
      { icon: '🌏', title: 'APAC Coverage', description: 'Teams available for APAC business hours collaboration.' },
      { icon: '🎓', title: 'Quality Engineering', description: 'Japanese-standard quality processes with nearshore efficiency.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, APPI-compliant, SOC 2-aligned security.' },
    ],
    services: s, industries: ind,
    faqs: faq('Fukuoka', 'Japan', '14h', 'JST with scheduled overlap'),
    localRate: '¥8,000-15,000', ourRate: '$30-50', savings: '60%+',
  },

  kampala: {
    slug: 'software-development-agency-kampala',
    cityName: 'Kampala',
    countryName: 'Uganda',
    flag: '🇺🇬',
    timezone: 'EAT (GMT+3)',
    currency: 'UGX/USD',
    title: { en: 'Software Development Agency Kampala | Call IT Dev' },
    description: { en: 'Software development agency for Kampala, Uganda. SaaS, web, mobile, AI. English-speaking engineers, from $20/hr. 70% savings vs US/UK.' },
    keywords: ['software development agency Kampala', 'IT outsourcing Uganda', 'web development Kampala', 'software company East Africa'],
    heroTitle: { en: 'Software Development Agency for Kampala' },
    heroSubtitle: { en: 'English-speaking engineers at competitive rates. 70% savings vs US/UK. Supporting East Africa\'s tech growth.' },
    whyTitle: { en: 'Why Kampala Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'English-Speaking Engineers', description: 'Native English developers for seamless global collaboration.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior devs at $20-38/hr vs $80-150/hr in US/UK.' },
      { icon: '🌍', title: 'East Africa Hub', description: 'Supporting Kampala\'s position as East Africa\'s tech capital.' },
      { icon: '⏰', title: 'European Overlap', description: 'GMT+3 provides strong overlap with European business hours.' },
      { icon: '🚀', title: 'Innovation Economy', description: 'Uganda\'s growing fintech and mobile money ecosystem.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR compliant, international security standards.' },
    ],
    services: s, industries: ind,
    faqs: faq('Kampala', 'Uganda', '7h', 'EAT with full European overlap'),
    localRate: '$80-150', ourRate: '$20-38', savings: '70%+',
  },

  manama: {
    slug: 'software-development-agency-manama',
    cityName: 'Manama',
    countryName: 'Bahrain',
    flag: '🇧🇭',
    timezone: 'AST (GMT+3)',
    currency: 'BHD/USD',
    title: { en: 'Software Development Agency Manama | Call IT Dev', ar: 'وكالة تطوير برمجيات المنامة | Call IT Dev' },
    description: { en: 'Software development agency for Manama, Bahrain. SaaS, web, mobile, AI, fintech. Bilingual engineers, from $25/hr. 60% savings.', ar: 'وكالة تطوير برمجيات للمنامة، البحرين. SaaS، ويب، موبايل، ذكاء اصطناعي. مهندسون ثنائيو اللغة، من 25$/ساعة.' },
    keywords: ['software development agency Manama', 'IT outsourcing Bahrain', 'fintech development Bahrain', 'web development Manama'],
    heroTitle: { en: 'Software Development Agency for Manama', ar: 'وكالة تطوير برمجيات للمنامة' },
    heroSubtitle: { en: 'Arabic & English engineers at nearshore rates. 60% savings. Supporting Bahrain\'s fintech vision.', ar: 'مهندسون عرب وإنجليز بأسعار nearshore. توفير 60%. دعم رؤية البحرين للتكنولوجيا المالية.' },
    whyTitle: { en: 'Why Manama Companies Choose Call IT Dev', ar: 'لماذا تختار شركات المنامة Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Arabic & English Teams', description: 'Bilingual engineers for Gulf market applications.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at $25-45/hr vs $70-130/hr locally.' },
      { icon: '🏦', title: 'FinTech Expertise', description: 'Supporting Bahrain\'s position as the Gulf\'s fintech hub.' },
      { icon: '⏰', title: 'GMT+3 Alignment', description: '2h difference with Morocco — strong overlap for collaboration.' },
      { icon: '🌍', title: 'GCC Market Access', description: 'Applications serving Bahrain, Saudi Arabia, UAE, and the wider Gulf.' },
      { icon: '🛡️', title: 'CBB Compliant', description: 'Central Bank of Bahrain regulations, PCI-DSS, ISO 27001.' },
    ],
    services: s, industries: ind,
    faqs: faq('Manama', 'Bahrain', '5h', 'AST with strong CET overlap'),
    localRate: '$70-130', ourRate: '$25-45', savings: '60%+',
  },

  montevideo: {
    slug: 'software-development-agency-montevideo',
    cityName: 'Montevideo',
    countryName: 'Uruguay',
    flag: '🇺🇾',
    timezone: 'UYT (GMT-3)',
    currency: 'UYU/USD',
    title: { en: 'Software Development Agency Montevideo | Call IT Dev', es: 'Agencia Desarrollo Software Montevideo | Call IT Dev' },
    description: { en: 'Software development agency for Montevideo, Uruguay. SaaS, web, mobile, AI. Bilingual engineers, from $28/hr. 55% savings vs US rates.', es: 'Agencia de desarrollo de software para Montevideo. SaaS, web, móvil, IA. Ingenieros bilingües, desde 28 $/h. 55% ahorro.' },
    keywords: ['software development agency Montevideo', 'IT outsourcing Uruguay', 'desarrollo software Uruguay', 'nearshore development Latin America'],
    heroTitle: { en: 'Software Development Agency for Montevideo', es: 'Agencia de Desarrollo de Software para Montevideo' },
    heroSubtitle: { en: 'Bilingual engineers at nearshore rates. 55% savings vs US. Supporting Uruguay\'s tech leadership in Latin America.', es: 'Ingenieros bilingües a precios nearshore. 55% ahorro vs EEUU. Apoyando el liderazgo tecnológico de Uruguay.' },
    whyTitle: { en: 'Why Montevideo Companies Choose Call IT Dev', es: 'Por Qué las Empresas de Montevideo Eligen Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Bilingual Engineers', description: 'Spanish and English-speaking developers for Americas markets.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at $28-48/hr vs $80-150/hr in US.' },
      { icon: '🌎', title: 'US Timezone Overlap', description: 'Full overlap with US Eastern time for real-time collaboration.' },
      { icon: '🏆', title: 'Latin America Leader', description: 'Uruguay ranks #1 in Latin America for digital governance and innovation.' },
      { icon: '🎓', title: 'Top STEM Talent', description: 'Access to Uruguay\'s highly educated engineering workforce.' },
      { icon: '🛡️', title: 'Data Protection', description: 'Uruguay has EU-recognized data protection adequacy. ISO 27001, GDPR aligned.' },
    ],
    services: s, industries: ind,
    faqs: faq('Montevideo', 'Uruguay', '10h', 'UYT with US Eastern overlap'),
    localRate: '$80-150', ourRate: '$28-48', savings: '55%+',
  },
};
